import transactionService from "../../services/api/transactionService";
import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { QUERY_CONFIGS } from "../../lib/react-query/config";

// Transaction Query Keys
export const TransactionQueryKeys = {
  all: ["transactions"],
  list: (filters) => [...TransactionQueryKeys.all, "list", filters],
  exportPdf: (filters) => [...TransactionQueryKeys.all, "exportPdf", filters],
  exportExcel: (filters) => [
    ...TransactionQueryKeys.all,
    "exportExcel",
    filters,
  ],
  recent: (limit) => [...TransactionQueryKeys.all, "recent", limit],
  totalRevenue: () => [...TransactionQueryKeys.all, "totalRevenue"],
  totalDisbursements: () => [...TransactionQueryKeys.all, "totalDisbursements"],
  authorizeWithdrawal: () => [
    ...TransactionQueryKeys.all,
    "authorizeWithdrawal",
  ],
  managementList: (filters) => [
    ...TransactionQueryKeys.all,
    "managementList",
    filters,
  ],
  reassignTransaction: () => [
    ...TransactionQueryKeys.all,
    "reassignTransaction",
  ],
  splitTransaction: () => [...TransactionQueryKeys.all, "splitTransaction"],
};

export const useGetAllTransactions = (filters = {}, options = {}) => {
  const {
    searchQuery = "",
    startDate = "",
    endDate = "",
    serviceCodes = [],
    page = 0,
    size = 10,
  } = filters;

  const filteredFilters = Object.fromEntries(
    Object.entries({
      searchQuery,
      startDate,
      endDate,
      serviceCodes,
      page,
      size,
    }).filter(([_, value]) => {
      if (Array.isArray(value)) return value.length > 0;
      return value !== "" && value != null;
    }),
  );
  const enabled =
    typeof options?.enabled === "boolean" ? options.enabled : true;

  return useQuery({
    queryKey: TransactionQueryKeys.list(filteredFilters),
    queryFn: () => transactionService.listAllTransactions(filteredFilters),
    enabled,
    placeholderData: keepPreviousData,
    ...QUERY_CONFIGS.STATIC,
  });
};

export const useGetManagementTransactions = (filters = {}, options = {}) => {
  const {
    searchQuery = "",
    startDate = "",
    endDate = "",
    serviceCodes = [],
    merchantId = "",
    page = 0,
    size = 10,
  } = filters;

  const filteredFilters = Object.fromEntries(
    Object.entries({
      searchQuery,
      startDate,
      endDate,
      serviceCodes,
      merchantId,
      page,
      size,
    }).filter(([_, value]) => {
      if (Array.isArray(value)) return value.length > 0;
      return value !== "" && value != null;
    }),
  );
  const enabled =
    typeof options?.enabled === "boolean" ? options.enabled : true;
  return useQuery({
    queryKey: TransactionQueryKeys.managementList(filteredFilters),
    queryFn: () =>
      transactionService.getManagementTransactions(filteredFilters),
    enabled,
    placeholderData: keepPreviousData,
    ...QUERY_CONFIGS.STATIC,
  });
};

export const useExportTransactionsPdf = (filters = {}) => {
  const {
    searchQuery = "",
    startDate = "",
    endDate = "",
    serviceCodes = [],
  } = filters;

  const filteredFilters = Object.fromEntries(
    Object.entries({
      searchQuery,
      startDate,
      endDate,
      serviceCodes,
    }).filter(([_, value]) => {
      if (Array.isArray(value)) return value.length > 0;
      return value !== "" && value != null;
    }),
  );

  return useMutation({
    mutationKey: TransactionQueryKeys.exportPdf(filteredFilters),
    mutationFn: () => transactionService.exportTransactionsPdf(filteredFilters),
  });
};

export const useExportManagementTransactionsPdf = (filters = {}) => {
  const {
    searchQuery = "",
    startDate = "",
    endDate = "",
    serviceCodes = [],
    merchantId = "",
    page = 0,
    size = 10,
  } = filters;

  const filteredFilters = Object.fromEntries(
    Object.entries({
      searchQuery,
      startDate,
      endDate,
      serviceCodes,
      merchantId,
      page,
      size,
    }).filter(([_, value]) => {
      if (Array.isArray(value)) return value.length > 0;
      return value !== "" && value != null;
    }),
  );

  return useMutation({
    mutationKey: TransactionQueryKeys.exportPdf(filteredFilters),
    mutationFn: () =>
      transactionService.exportManagementTransactionsPdf(filteredFilters),
  });
};

export const useExportTransactionsExcel = (filters = {}) => {
  const {
    searchQuery = "",
    startDate = "",
    endDate = "",
    serviceCodes = [],
  } = filters;

  const filteredFilters = Object.fromEntries(
    Object.entries({
      searchQuery,
      startDate,
      endDate,
      serviceCodes,
    }).filter(([_, value]) => {
      if (Array.isArray(value)) return value.length > 0;
      return value !== "" && value != null;
    }),
  );

  return useMutation({
    mutationKey: TransactionQueryKeys.exportExcel(filteredFilters),
    mutationFn: () =>
      transactionService.exportTransactionsExcel(filteredFilters),
  });
};

export const useExportManagementTransactionsExcel = (filters = {}) => {
  const {
    searchQuery = "",
    startDate = "",
    endDate = "",
    serviceCodes = [],
    merchantId = "",
    page = 0,
    size = 10,
  } = filters;

  const filteredFilters = Object.fromEntries(
    Object.entries({
      searchQuery,
      startDate,
      endDate,
      serviceCodes,
      merchantId,
      page,
      size,
    }).filter(([_, value]) => {
      if (Array.isArray(value)) return value.length > 0;
      return value !== "" && value != null;
    }),
  );

  return useMutation({
    mutationKey: TransactionQueryKeys.exportExcel(filteredFilters),
    mutationFn: () =>
      transactionService.exportManagementTransactionsExcel(filteredFilters),
  });
};

export const useGetRecentPayouts = (limit) => {
  return useQuery({
    queryKey: TransactionQueryKeys.recent(limit),
    queryFn: () => transactionService.getRecentPayouts(limit),
    enabled: true,
    keepPreviousData: true,
    ...QUERY_CONFIGS.STATIC,
  });
};

export const useGetTotalRevenue = () => {
  return useQuery({
    queryKey: TransactionQueryKeys.totalRevenue(),
    queryFn: transactionService.getTotalRevenue,
    enabled: true,
    ...QUERY_CONFIGS.STATIC,
  });
};

export const useGetTotalDisbursements = () => {
  return useQuery({
    queryKey: TransactionQueryKeys.totalDisbursements(),
    queryFn: transactionService.getTotalDisbursments,
    enabled: true,
    ...QUERY_CONFIGS.STATIC,
  });
};

export const useAuthorizeWithdrawal = () => {
  return useMutation({
    mutationKey: TransactionQueryKeys.authorizeWithdrawal(),
    mutationFn: (request) => transactionService.authorizeWithdrawal(request),
  });
};

export const useReassignTransaction = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: TransactionQueryKeys.reassignTransaction(),
    mutationFn: (request) => transactionService.reassignTransaction(request),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: TransactionQueryKeys.all });
      const previousTransactionQueries = queryClient.getQueriesData({
        queryKey: TransactionQueryKeys.all,
      });

      return { previousTransactionQueries };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousTransactionQueries) {
        context.previousTransactionQueries.forEach(([queryKey, queryData]) => {
          queryClient.setQueryData(queryKey, queryData);
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: TransactionQueryKeys.all });
    },
  });
};

export const useSplitTransaction = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: TransactionQueryKeys.splitTransaction(),
    mutationFn: ({ transactionId, channels }) =>
      transactionService.splitTransaction({ transactionId, channels }),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: TransactionQueryKeys.all });
      const previousTransactionQueries = queryClient.getQueriesData({
        queryKey: TransactionQueryKeys.all,
      });

      return { previousTransactionQueries };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousTransactionQueries) {
        context.previousTransactionQueries.forEach(([queryKey, queryData]) => {
          queryClient.setQueryData(queryKey, queryData);
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: TransactionQueryKeys.all });
    },
  });
};
